import RangeSlider from '@gui/components/generic/RangeSider';
import { getSentence } from '@gui/constants/sentences';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { useDataLayerSlice } from '@gui/store/hooks';
import usePersistentStore, {
    getLayerState,
    getVectorialScalarMode,
    useGlobalBucket,
    useLayersBucket,
} from '@gui/store/persistent/persistentStore';
import { BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import { Box, Divider, IconButton, Slider, Tab, Tabs, Typography } from '@mui/material';
import { AlertCircle, AlignVerticalCenter01, ChevronSelectorVertical } from '@untitledui-pro/icons/line';
import { MIXED, SIM_NOISE, SIM_SUN, SIM_SUN4D, SIM_TARGET, SIM_VVA, SIM_WIND } from '@visualizer/types';
import type { ReactNode } from 'react';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import Layer from 'Simulations/Layer';
import Grid from 'Geodata/grid';
import { formatMetricNumber } from 'Utils/MetricFormatting';
import { getGrid, LoadingStatus, useLoadingStore } from 'store/LoadingStore';
import { highlightSx, useAutoHighlight } from '../generic/useAutoHighlight';
import { VisualizationElementId } from '@rendering/types/VisualizationElements';
import { SensorData } from 'Simulations/SensorPoint';
import { isEnergyLabel } from '@gui/constants/Data';
import { useDesignSchemesMetricRows } from './Metrics/DesignSchemes';
import { MetricRow, MetricTable } from './Metrics/MetricTable';
import CustomTooltip from '../generic/CustomTooltip';

type PerformanceMetricsProps = {
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    panelsExpanded: Record<string, boolean>;
    style?: CSSProperties;
};

const KBOB_CH_CONSUMER_MIX_KG_CO2EQ_PER_KWH = 0.125;
const KBOB_SWISS_PV_KG_CO2EQ_PER_KWH = 0.037;
const PERCENT_NORMALIZED_TOLERANCE = 0.001;

// Used to avoid depth loops (When you pass [] directly to Zustand it creates a new array and it's problematic):
const EMPTY_MIXER_ELEMENTS: any[] = [];

const PerformanceMetricsPanel: React.FC<PerformanceMetricsProps> = ({ isCollapsed, onToggleCollapse, panelsExpanded, style }) => {
    // Controllers:
    const pipelineController = ControllerManager.getInstance().getPipelineController();

    // Zustand Variables:
    const activeLayerId = useLayersBucket((s) => s.activeLayerID);
    const mixerElements = useLayersBucket((s) => s.layerStates.mixed?.mixerElements ?? EMPTY_MIXER_ELEMENTS);
    const isMultimode = useGlobalBucket((s) => s.data.multimode);
    const isMixer = useGlobalBucket((s) => s.data.isMixerMode);
    const loadingStatus = useLoadingStore((s) => s.loadingStatus);
    const remappedMinMax = useGlobalBucket((s) => s.overlays.remappedMinMax);
    const yearlyEnergyDemand = useGlobalBucket((s) => s.solarEnergyResources.yearlyDemand);
    const activeVisElement = usePersistentStore((s) => s.getActiveLayerState()?.element?.SEL_VIS_ELEMENT);
    const layerPerformanceMetrics = usePersistentStore((s) => s.getActiveLayerState()?.performanceMetrics);
    const filter = usePersistentStore((s) => s.getActiveLayerState()?.filter);
    const activeSensorPoints = usePersistentStore((s) => s.getActiveLayerState()?.pipeline?.executionCount ?? 0);
    const activeResultId = useDataLayerSlice().data?.activeResultId;
    const expandedCount = Object.values(panelsExpanded).filter(Boolean).length;
    const disableToggle = !isCollapsed && expandedCount === 1;
    const ahActiveSensorPoints = useAutoHighlight(activeSensorPoints);

    // Zustand Actions:
    const setPerformanceMetricsPanel = usePersistentStore((s) => s.setPerformanceMetricsPanel);

    // React context:
    const [layer, setLayer] = useState(() => getGrid()?.getActiveLayer());

    // Whenever the visualizer becomes ready, update the layer reference:
    useEffect(() => {
        if (loadingStatus === LoadingStatus.VISUALIZER_READY) {
            const grid = getGrid();
            const activeLayer = grid?.getActiveLayer();
            setLayer(activeLayer);
        }
    }, [loadingStatus]);

    // Re-render when active layer changes:
    useEffect(() => {
        const grid = getGrid();
        const newLayer = grid?.getActiveLayer();
        setLayer(newLayer);
    }, [activeLayerId, activeResultId]);

    // Handlers:
    const handleNoiseChange = (newValue: number) => {
        setPerformanceMetricsPanel({
            thresholdLevel: newValue,
        });
    };

    const handleWindRangeChange = (next: [number, number]) => {
        setPerformanceMetricsPanel({
            thresholdLevel: next,
        });
    };

    const handleMetricThresholdChange = (newValue: number) => {
        setPerformanceMetricsPanel({
            thresholdLevel: newValue,
        });
    };

    // Rendering helpers:
    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

    const metricPrefixes = ['', 'k', 'M', 'G', 'T', 'P'];
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const getMidpoint = (min: number, max: number) => min + (max - min) / 2;
    const getSliderStep = (min: number, max: number) => {
        const span = Math.abs(max - min);
        if (!Number.isFinite(span) || span === 0) return 1;
        return span / 100;
    };
    const formatSliderValue = (value: number) => formatMetricNumber(value);
    const normalizeMetricUnit = (unit?: string) =>
        unit
            ?.replace(/\/year\b/g, '/y')
            .replace(/\bm2\b/g, 'm²')
            .replace(/\/m2\b/g, '/m²');
    const isPercentUnit = (unit?: string) => (unit ?? '').replace(/\s/g, '').includes('%');
    const isNormalizedPercentRange = (unit: string | undefined, min: number, max: number) =>
        isPercentUnit(unit) &&
        Number.isFinite(min) &&
        Number.isFinite(max) &&
        min >= -PERCENT_NORMALIZED_TOLERANCE &&
        max <= 1 + PERCENT_NORMALIZED_TOLERANCE;
    const getYearlySolarEnergyUnit = (layer: Layer) => {
        const currentUnit = normalizeMetricUnit(layer.getResult().unitOfMeasure);
        const geodataId = layer.parentSimulationSet?.parentGeodata?.id;
        const yearlySolarLayers = getGrid()?.findLayersByKind(SIM_SUN) ?? [];
        const yearlyLayer = yearlySolarLayers.find((candidate) => {
            if (candidate.parentSimulationSet?.parentGeodata?.id !== geodataId) return false;
            return candidate.results.some((result) => isEnergyLabel(result?.resultLabel));
        });
        const yearlyEnergyResult = yearlyLayer?.results.find((result) => isEnergyLabel(result?.resultLabel));
        return normalizeMetricUnit(yearlyEnergyResult?.unitOfMeasure) ?? currentUnit;
    };

    const formatMetricValue = (value: number, unit?: string, allowUnitScaling = true) => {
        const safeValue = Number.isFinite(value) ? value : 0;
        const normalizedUnit = normalizeMetricUnit(unit);

        if (!normalizedUnit || !allowUnitScaling) {
            const cleanUnit = normalizedUnit?.replace(/^\[/, '').replace(/\]$/, '');
            return { value: formatMetricNumber(safeValue), unit: cleanUnit };
        }

        const [unitMain, ...unitSuffixParts] = normalizedUnit.split('/');
        const suffix = unitSuffixParts.length ? `/${unitSuffixParts.join('/')}` : '';

        let baseUnit = unitMain;
        let prefixIndex = 0;

        for (let i = metricPrefixes.length - 1; i > 0; i -= 1) {
            const prefix = metricPrefixes[i];
            if (prefix && unitMain.startsWith(prefix)) {
                prefixIndex = i;
                baseUnit = unitMain.slice(prefix.length) || unitMain;
                break;
            }
        }

        let scaledValue = safeValue;
        let nextPrefixIndex = prefixIndex;
        while (Math.abs(scaledValue) >= 1000 && nextPrefixIndex < metricPrefixes.length - 1) {
            scaledValue /= 1000;
            nextPrefixIndex += 1;
        }

        const formattedValue = formatMetricNumber(scaledValue);
        const formattedUnit = `${metricPrefixes[nextPrefixIndex]}${baseUnit}${suffix}`;

        return { value: formattedValue, unit: formattedUnit };
    };

    const buildLayerMetrics = (layer: Layer) => {
        const frame = layer.getActiveFrame();
        const layerState = getLayerState(layer.getUID());

        // =====================================
        // COMPUTE METRICS
        // =====================================

        // For MultiMode:
        const multiActive = isMultimode && isMixer;
        const internalMixerElements = multiActive
            ? ControllerManager.getInstance().getMixerController().getMixerLayer().getInternalMixerElements()
            : [];
        const internalMixerElement = internalMixerElements.find((me) => me.layerId === layer.getUID());

        // For all:
        const lastPipeResult =
            multiActive && internalMixerElement ? internalMixerElement.layerPipelineResult.data : pipelineController.getLastResult()?.data;
        const activeSensors = lastPipeResult ?? frame.getSensors() ?? [];

        // 1. Compute Active Sensors:
        const activeSPs = activeSensors?.length;
        const totalSPs = frame.getSensors()?.length;

        // 2. Compute Quad/Box surface and Voxel volume:
        const selectedVisElement = layer.getUID() === activeLayerId ? activeVisElement : layerState.element?.SEL_VIS_ELEMENT;
        const isQuadSelected = selectedVisElement === VisualizationElementId.Quad;
        const isVoxelSelected = selectedVisElement === VisualizationElementId.Voxel;
        const isBoxSelected = selectedVisElement === VisualizationElementId.Box;
        const gridRes = Number.isFinite(Number(Grid.gridRes)) ? Number(Grid.gridRes) : 0;
        const singleSurface = gridRes * gridRes;
        const singleVolume = gridRes * gridRes * gridRes;
        let totalSurface = -1;
        let totalVolume = -1;
        if (isQuadSelected || isBoxSelected) {
            totalSurface = activeSPs * singleSurface;
        }
        if (isVoxelSelected) {
            totalVolume = activeSPs * singleVolume;
        }

        // 3. Summed Value of Sensors:
        const summedValue =
            activeSensors.reduce((sum, sensor) => {
                return sum + sensor.getScalarValue();
            }, 0) ?? 0;
        const averageValue = activeSPs > 0 ? summedValue / activeSPs : 0;
        const weightedSurfaceSum = summedValue * singleSurface;

        // 4. Percent of Sensors in an established Range:

        // 4.1. Sun, Free & Target Views:
        const [domainMin, domainMax] = layer.getResultDomain(getVectorialScalarMode());
        const [remappedMin, remappedMax] = remappedMinMax;
        const hasRemappedRange =
            Number.isFinite(remappedMin) && Number.isFinite(remappedMax) && Number(remappedMin) !== Number(remappedMax);
        const resolvedDomainMin = hasRemappedRange ? Number(remappedMin) : Number.isFinite(domainMin) ? domainMin : 0;
        const resolvedDomainMax = hasRemappedRange ? Number(remappedMax) : Number.isFinite(domainMax) ? domainMax : resolvedDomainMin;
        const displaysNormalizedPercent = isNormalizedPercentRange(layer.getResult().unitOfMeasure, resolvedDomainMin, resolvedDomainMax);
        const midpointValue = getMidpoint(resolvedDomainMin, resolvedDomainMax);
        const sliderStep = getSliderStep(resolvedDomainMin, resolvedDomainMax);

        const thresholdLevel = layerPerformanceMetrics?.thresholdLevel;
        const defaultThresholdLevel = layer.getSimKind() === SIM_NOISE ? 60 : layer.getSimKind() === SIM_WIND ? [0, 2.5] : null;
        const resolvedThresholdLevel = thresholdLevel ?? defaultThresholdLevel;

        // 4.2.  Wind:
        const windRange: [number, number] = Array.isArray(resolvedThresholdLevel) ? (resolvedThresholdLevel as [number, number]) : [0, 2.5];

        // 5. Solar module (PV & SHG):
        const type = 'y';
        const pipelineBlocks = layer.getPipeline().getBlocks();
        const pvBlock = pipelineBlocks.find((b) => b.type === BlockKeys.PvPotentials && b.getParams()?.enabled !== false);
        const shgBlock = pipelineBlocks.find((b) => b.type === BlockKeys.ShgPotentials && b.getParams()?.enabled !== false);
        const pvParams = pvBlock?.getParams?.() as { panelEfficiency?: number; surfaceCoverage?: number } | undefined;
        const shgParams = shgBlock?.getParams?.() as { gValue?: number; demand?: number } | undefined;
        const activeProducedEnergy =
            pvBlock != null ? summedValue * ((pvParams?.panelEfficiency ?? 100) / 100) * ((pvParams?.surfaceCoverage ?? 100) / 100) : 0;
        const passiveTransmittedEnergy = shgBlock != null ? summedValue * ((shgParams?.gValue ?? 100) / 100) : 0;
        const shgMatchedDemand = Math.max(0, Number(shgParams?.demand ?? 0));
        const demandReduction = shgMatchedDemand > 0 ? (passiveTransmittedEnergy / shgMatchedDemand) * 100 : 0;
        const demandCoverage = yearlyEnergyDemand > 0 ? (activeProducedEnergy / yearlyEnergyDemand) * 100 : 0;
        const savedOperationalEmissionsTons =
            (passiveTransmittedEnergy * KBOB_CH_CONSUMER_MIX_KG_CO2EQ_PER_KWH) / 1000 +
            (activeProducedEnergy * (KBOB_CH_CONSUMER_MIX_KG_CO2EQ_PER_KWH - KBOB_SWISS_PV_KG_CO2EQ_PER_KWH)) / 1000;

        // =====================================
        // UI
        // =====================================

        // 1. Active Sensors:
        const activeSPsDisplay = Number.isFinite(activeSPs) ? formatMetricNumber(activeSPs as number) : '—';
        const totalSPsDisplay = Number.isFinite(totalSPs) ? formatMetricNumber(totalSPs as number) : '—';
        const surfaceMetricTooltip = 'Active Sensor Points * area of one element (grid resolution squared)';
        const volumeMetricTooltip = 'Active Sensor Points * the volume of one element (grid resolution cubed)';
        const rows: MetricRow[] = !multiActive
            ? [
                  {
                      label: 'Active Sensor Points',
                      value: (
                          <Box
                              ref={ahActiveSensorPoints.ref}
                              className="auto-highlight-text"
                              sx={highlightSx({ highlightSelf: true, highlightSelectors: ['strong'] })}
                          >
                              <div>{activeSPsDisplay}</div>
                          </Box>
                      ),
                      unit: `of ${totalSPsDisplay}`,
                      wrapUnit: false,
                      highlight: true,
                  },
              ]
            : [];
        let controls: ReactNode | null = null;
        const trailingRows: MetricRow[] = [];

        // 2. Quad/Box Surface:
        if (totalSurface >= 0) {
            rows.push({
                label: isQuadSelected ? 'Quads Surface' : 'Box Surface',
                value: formatMetricNumber(totalSurface),
                unit: 'm²',
                tooltip: surfaceMetricTooltip,
                dividerAfter: true,
            });
        }

        if (totalVolume >= 0) {
            rows.push({
                label: 'Voxels Volume',
                value: formatMetricNumber(totalVolume),
                unit: 'm³',
                tooltip: volumeMetricTooltip,
                dividerAfter: true,
            });
        }

        // 3. Average + Summed Values:
        const setSingleValueSliderControl = (
            currentValue: number,
            onChange: (value: number) => void,
            min: number,
            max: number,
            step: number,
            unitLabel = normalizeMetricUnit(layer.getResult().unitOfMeasure),
            valueLabelFormat: (value: number) => string = formatSliderValue
        ) => {
            if (multiActive) return;

            controls = (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 0 }}>
                    <Slider
                        className="panel-slider"
                        sx={{ flex: 1, minWidth: 0 }}
                        value={currentValue}
                        style={{ marginRight: '10px', marginLeft: '5px' }}
                        onChange={(_, v) => onChange(v as number)}
                        min={min}
                        max={max}
                        step={step}
                        valueLabelDisplay="on"
                        valueLabelFormat={valueLabelFormat}
                        color="primary"
                        size="small"
                    />
                    <Typography variant="body2" sx={{ minWidth: '26%', textAlign: 'right', pr: 0 }}>
                        {unitLabel}
                    </Typography>
                </Box>
            );
        };

        const pushSumMetricRow = (label: string, value: ReactNode, unit: string, tooltipTitle: string) => {
            rows.push({
                label,
                value,
                unit,
                tooltip: tooltipTitle,
            });
        };

        const pushThresholdMetricRow = ({
            label,
            tooltipTitle,
            value,
            onChange,
            comparator = 'gte',
            min = resolvedDomainMin,
            max = resolvedDomainMax,
            step = sliderStep,
            sensors = activeSensors,
            displayAsNormalizedPercent = false,
        }: {
            label: string;
            tooltipTitle: string;
            value: number;
            onChange: (value: number) => void;
            comparator?: 'gte' | 'lte';
            min?: number;
            max?: number;
            step?: number;
            sensors?: any[];
            displayAsNormalizedPercent?: boolean;
        }) => {
            const threshold = clamp(value, min, max);
            const matchingSensors = sensors.filter((sensor: any) =>
                comparator === 'lte' ? sensor.getScalarValue() <= threshold : sensor.getScalarValue() >= threshold
            );
            const percent = sensors.length ? (matchingSensors.length / sensors.length) * 100 : 0;
            const displayValue = displayAsNormalizedPercent ? percent * 100 : percent;
            const sliderLabelFormatter = displayAsNormalizedPercent
                ? (sliderValue: number) => formatMetricNumber(sliderValue * 100)
                : formatSliderValue;

            rows.push({
                label,
                value: formatMetricNumber(displayValue),
                unit: '%',
                tooltip: tooltipTitle,
            });

            setSingleValueSliderControl(
                threshold,
                onChange,
                min,
                max,
                step,
                normalizeMetricUnit(layer.getResult().unitOfMeasure),
                sliderLabelFormatter
            );
        };

        const pushAverageMetricRow = (label: string, unitOverride?: string) => {
            const baseUnit = unitOverride ?? layer.getResult().unitOfMeasure;
            const averageUnit = baseUnit || '';
            const averageDisplayValue = displaysNormalizedPercent ? averageValue * 100 : averageValue;
            const { value, unit } = formatMetricValue(averageDisplayValue, averageUnit, false);
            const normalizedUnit = unit?.replace(/^\[/, '').replace(/\]$/, '') ?? '';

            rows.push({
                label,
                value,
                unit: normalizedUnit,
                tooltip: 'Average SensorValue over active Sensor Points',
            });
        };

        switch (layer.getSimKind()) {
            case SIM_SUN:
            case SIM_SUN4D: {
                const resultLabel = layer.getResult().resultLabel;
                if (resultLabel === 'Energy') {
                    const yearlySolarEnergyUnit = getYearlySolarEnergyUnit(layer);
                    pushAverageMetricRow(`Average ${capitalize(type)} Solar Energy`, yearlySolarEnergyUnit);
                    const yearlyTotalEnergyUnit = yearlySolarEnergyUnit?.replace('/m²', '') || yearlySolarEnergyUnit || `kWh/${type}`;
                    const totalSolarEnergy = summedValue * singleSurface;
                    const { value, unit } = formatMetricValue(totalSolarEnergy, yearlyTotalEnergyUnit, false);
                    pushSumMetricRow(
                        `Tot ${capitalize(type)} Solar Energy`,
                        value,
                        unit ?? '',
                        'Sum(SolarEnergy/m²/y * QuadSurface) -> kWh/y'
                    );
                    pushThresholdMetricRow({
                        label: `Points above Solar th`,
                        tooltipTitle: '% of Sensor Points above selected Solar Energy threshold',
                        value: typeof resolvedThresholdLevel === 'number' ? resolvedThresholdLevel : midpointValue,
                        onChange: handleMetricThresholdChange,
                        displayAsNormalizedPercent: displaysNormalizedPercent,
                    });
                    break;
                }

                if (resultLabel === 'Time') {
                    pushAverageMetricRow(`Average ${capitalize(type)} Solar Time`);
                    pushThresholdMetricRow({
                        label: `Points above Solar th`,
                        tooltipTitle: '% of Sensor Points above selected Solar Time threshold',
                        value: typeof resolvedThresholdLevel === 'number' ? resolvedThresholdLevel : midpointValue,
                        onChange: handleMetricThresholdChange,
                        displayAsNormalizedPercent: displaysNormalizedPercent,
                    });
                    break;
                }
                rows.push({ label: 'Frame data', value: 'No active frame data', unit: '' });
                break;
            }

            case SIM_VVA: {
                pushAverageMetricRow('Average Free-View');
                pushSumMetricRow('Tot Free-View', formatMetricNumber(weightedSurfaceSum), 'm²', 'Sum(SensorValue*QuadSurface)');
                pushThresholdMetricRow({
                    label: 'Points above Free-View th',
                    tooltipTitle: '% of Sensor Points above selected Free-View threshold',
                    value: typeof resolvedThresholdLevel === 'number' ? resolvedThresholdLevel : midpointValue,
                    onChange: handleMetricThresholdChange,
                    displayAsNormalizedPercent: displaysNormalizedPercent,
                });
                break;
            }

            case SIM_TARGET: {
                pushAverageMetricRow('Average Target-View');
                pushSumMetricRow('Tot Target-View', formatMetricNumber(weightedSurfaceSum), 'm²', 'Sum(SensorValue*QuadSurface)');
                pushThresholdMetricRow({
                    label: 'Points above Target th',
                    tooltipTitle: '% of Sensor Points above selected Target-View threshold',
                    value: typeof resolvedThresholdLevel === 'number' ? resolvedThresholdLevel : midpointValue,
                    onChange: handleMetricThresholdChange,
                    displayAsNormalizedPercent: displaysNormalizedPercent,
                });
                break;
            }

            case SIM_NOISE: {
                pushAverageMetricRow('Average Noise');
                pushThresholdMetricRow({
                    label: 'Points in Noise range',
                    tooltipTitle: '% of Sensor Points in Noise comfort-range',
                    value: typeof resolvedThresholdLevel === 'number' ? resolvedThresholdLevel : 60,
                    onChange: handleNoiseChange,
                    comparator: 'lte',
                    min: 0,
                    max: 120,
                    step: 1,
                });
                break;
            }

            case SIM_WIND: {
                pushAverageMetricRow('Average Wind');
                const sensors = frame.getSensors() ?? [];
                const [windMin, windMax] = windRange;
                const activeSensors = sensors.filter((sd: SensorData) => {
                    const val = sd.getScalarValue();
                    return val >= windMin && val <= windMax;
                });
                const percent = sensors.length ? (activeSensors.length / sensors.length) * 100 : 0;

                rows.push({
                    label: 'Points in Wind range',
                    value: formatMetricNumber(percent),
                    unit: '%',
                    tooltip: '% of Sensor Points in Wind comfort-range',
                });
                if (!multiActive)
                    controls = (
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 0 }}>
                            <RangeSlider
                                showValues={true}
                                value={windRange as [number, number]}
                                lastIndex={10}
                                step={0.5}
                                onChange={handleWindRangeChange}
                                sx={{ flex: 1, mr: 1, ml: 1, minWidth: 0 }}
                            />
                            <Typography variant="body2" sx={{ minWidth: '26%', textAlign: 'right', pr: 0 }}>
                                {normalizeMetricUnit(layer.getResult().unitOfMeasure)}
                            </Typography>
                        </Box>
                    );
                break;
            }

            case MIXED:
                if (isMultimode)
                    rows.push({
                        label: 'Info',
                        value: 'Add layers to see their metrics in MultiMode.',
                        unit: '-',
                        wrap: true,
                        hideUnit: true,
                    });
                else
                    rows.push({
                        label: 'Info',
                        value: 'Mixed layers do not have specific metrics.',
                        unit: '-',
                        wrap: true,
                        hideUnit: true,
                    });
                break;

            default:
                rows.push({ label: 'Info', value: 'Unknown simulation type.', unit: '' });
                break;
        }

        if (shgBlock) {
            const { value, unit } = formatMetricValue(passiveTransmittedEnergy, 'kWh/y', false);
            trailingRows.push({ label: 'Passive Energy', tooltip: 'Passive Transmitted Energy', value, unit });
            trailingRows.push({ label: 'Demand Reduction', value: formatMetricNumber(demandReduction), unit: '%' });
        }

        if (pvBlock) {
            const { value, unit } = formatMetricValue(activeProducedEnergy, 'kWh/y', false);
            trailingRows.push({ label: 'Active Produced Energy', value, unit });
            trailingRows.push({ label: 'Demand Coverage', value: formatMetricNumber(demandCoverage), unit: '%' });
        }

        if (shgBlock || pvBlock) {
            trailingRows.push({
                label: 'Saved Emissions',
                value: formatMetricNumber(savedOperationalEmissionsTons),
                unit: 'tCO₂e/y',
                tooltip:
                    'Saved emissions = (Passive Transmitted Energy*0.125/1000) + (Active Produced Energy*(0.125-0.037)/1000), using KBOB factors [kg CO2-eq./kWh].',
            });
        }

        const appendSurfaceAtBottom = false;
        if (appendSurfaceAtBottom) {
            // Quad / Box surface:
            const selectedVisElement = layer.getUID() === activeLayerId ? activeVisElement : layerState.element?.SEL_VIS_ELEMENT;
            const isQuadSelected = selectedVisElement === VisualizationElementId.Quad;
            const isBoxSelected = selectedVisElement === VisualizationElementId.Box;
            if (isQuadSelected || isBoxSelected) {
                const gridRes = Number.isFinite(Number(Grid.gridRes)) ? Number(Grid.gridRes) : 0;
                const singleSurface = gridRes * gridRes;
                const totalSurface = activeSPs * singleSurface;
                rows.push({
                    label: isQuadSelected ? 'Quad Surface' : 'Box Surface',
                    value: formatMetricNumber(totalSurface),
                    unit: 'm²',
                });
            }
        }
        return { rows, controls, trailingRows };
    };

    const designSchemeMetricRows = useDesignSchemesMetricRows();

    // Component's HTML:
    const shouldUseMultimode = isMultimode && mixerElements.length > 0 && isMixer;
    const content = useMemo(() => {
        if (shouldUseMultimode) {
            return (
                <Box display="flex" flexDirection="column" gap={0}>
                    {mixerElements.map((m, i) => {
                        const layerRef = getGrid().searchLayerByUID(m.layerId);
                        if (!layerRef) return null;
                        const { rows, controls, trailingRows } = buildLayerMetrics(layerRef);

                        return (
                            <Box key={m.layerId} display="flex" flexDirection="column" gap={0}>
                                <MetricTable
                                    rows={rows}
                                    controls={controls}
                                    trailingRows={trailingRows}
                                    title={layerRef.name}
                                    showHeader={i === 0}
                                />
                                {i < mixerElements.length - 1 && <Divider sx={{ my: 0 }} />}
                            </Box>
                        );
                    })}
                    {designSchemeMetricRows.length > 0 && <MetricTable rows={designSchemeMetricRows} showHeader={false} />}
                </Box>
            );
        }
        if (layer) {
            const { rows, controls, trailingRows } = buildLayerMetrics(layer);
            return <MetricTable rows={rows} controls={controls} trailingRows={[...trailingRows, ...designSchemeMetricRows]} />;
        }
        return <Typography>No active layer.</Typography>;
    }, [
        designSchemeMetricRows,
        layer,
        shouldUseMultimode,
        mixerElements,
        layerPerformanceMetrics,
        remappedMinMax,
        activeSensorPoints,
        activeResultId,
        filter,
        isMultimode,
        yearlyEnergyDemand,
        activeVisElement,
    ]);

    const [activeTab, setActiveTab] = useState<'metrics' | 'scale'>('metrics');

    const handleCollapseToggle = () => {
        if (onToggleCollapse) onToggleCollapse();
    };

    const metricsContent = (
        <Box display="flex" flexDirection="column" gap={1}>
            {content}
        </Box>
    );

    return (
        <div
            className={`data-panel data-panel--performance ${isCollapsed ? 'data-panel--collapsed' : ''}`}
            style={style}
            data-guided-tour="guided-tour-metrics-panel"
        >
            <div className="data-panel-tabs">
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, value) => {
                                if (isCollapsed) return;
                                setActiveTab(value as 'metrics' | 'scale');
                            }}
                            sx={{
                                '& .MuiTabs-flexContainer': {
                                    gap: 3.5,
                                    mr: 1.5,
                                },
                            }}
                            aria-label="Performance metrics panel tabs"
                        >
                            <Tab
                                value="metrics"
                                label={
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        METRICS
                                        <CustomTooltip side="right" title={getSentence('Metrics.TabTitle.Icon.Tooltip')}>
                                            <AlertCircle className="icon-size-small" />
                                        </CustomTooltip>
                                    </Box>
                                }
                                disabled={isCollapsed}
                            />
                            <Tab value="scale" label="SCALE" disabled={isCollapsed} />
                        </Tabs>
                    </Box>
                    <IconButton
                        data-testid="metrics-panel-collapse-toggle"
                        size="small"
                        onClick={handleCollapseToggle}
                        disabled={disableToggle}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                            },
                            '&:active': {
                                boxShadow: 'none',
                            },
                            '&:focus-visible': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                transition: 'transform 120ms ease',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            }}
                        >
                            {isCollapsed ? (
                                <ChevronSelectorVertical className="icon-size-small" />
                            ) : (
                                <AlignVerticalCenter01 className="icon-size-small" />
                            )}
                        </Box>
                    </IconButton>
                </Box>
            </div>

            {!isCollapsed && (
                <div className="data-panel-content">
                    <div className="data-panel-scroll">
                        {activeTab === 'metrics' ? (
                            metricsContent
                        ) : (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center',
                                    pt: 4,
                                }}
                            >
                                <Typography variant="h6">Coming soon!</Typography>
                            </Box>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PerformanceMetricsPanel;
